import { AudioCategory, CueId, SAMPLE_MANIFEST, CUE_CATEGORY } from './types';

/**
 * Palaestra Audio Engine v2.0
 *
 * Sample-based playback with WebAudio scheduling.
 * Falls back to synthesized tones when .mp3 files are missing.
 */
class AudioEngine {
    private ctx: AudioContext | null = null;
    private masterGain: GainNode | null = null;
    private categoryGains: Record<AudioCategory, GainNode | null> = {
        master: null,
        fx: null,
        voice: null,
        ui: null,
    };

    private buffers: Map<string, AudioBuffer> = new Map();
    private activeSources: Set<AudioScheduledSourceNode> = new Set();
    private timeouts: number[] = [];
    private preloaded = false;

    // ─── INIT ────────────────────────────────────────────

    private init() {
        if (this.ctx) return;

        const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
        this.ctx = new AudioCtx();

        this.masterGain = this.ctx.createGain();
        this.masterGain.connect(this.ctx.destination);

        (['fx', 'voice', 'ui'] as const).forEach(cat => {
            if (!this.ctx) return;
            const gn = this.ctx.createGain();
            gn.connect(this.masterGain!);
            this.categoryGains[cat] = gn;
        });
    }

    public resume() {
        if (!this.ctx) this.init();
        if (this.ctx?.state === 'suspended') {
            this.ctx.resume().then(() => {
                console.log('🔊 AudioEngine: Context resumed');
            }).catch(console.error);
        }
    }

    // ─── VOLUME ──────────────────────────────────────────

    public setVolume(category: AudioCategory, value: number) {
        if (!this.ctx) this.init();
        const target = category === 'master' ? this.masterGain : this.categoryGains[category];
        if (target && this.ctx) {
            target.gain.setTargetAtTime(Math.max(0, Math.min(1, value)), this.ctx.currentTime, 0.05);
        }
    }

    // ─── PRELOAD ─────────────────────────────────────────

    /**
     * Fetch + decode all known audio samples into AudioBuffers.
     * Safe to call multiple times (idempotent).
     */
    public async preload(): Promise<void> {
        if (this.preloaded) return;
        if (!this.ctx) this.init();

        const entries = Object.entries(SAMPLE_MANIFEST) as [CueId, string][];

        const results = await Promise.allSettled(
            entries.map(async ([id, url]) => {
                if (this.buffers.has(id)) return;
                try {
                    const res = await fetch(url);
                    if (!res.ok) throw new Error(`HTTP ${res.status}`);
                    const arrayBuf = await res.arrayBuffer();
                    const audioBuf = await this.ctx!.decodeAudioData(arrayBuf);
                    this.buffers.set(id, audioBuf);
                    console.log(`🎵 Loaded: ${id}`);
                } catch (e) {
                    console.warn(`⚠️ Audio: could not load ${url} — using synth fallback`, e);
                }
            })
        );

        const loaded = results.filter(r => r.status === 'fulfilled').length;
        console.log(`🔊 AudioEngine: Preloaded ${loaded}/${entries.length} samples`);
        this.preloaded = true;
    }

    // ─── SAMPLE PLAYBACK ─────────────────────────────────

    /**
     * Play a decoded AudioBuffer through a category gain node.
     * @param when – optional AudioContext.currentTime offset for scheduling
     */
    private playBuffer(id: string, category: AudioCategory, when?: number) {
        if (!this.ctx || !this.buffers.has(id)) return false;

        const source = this.ctx.createBufferSource();
        source.buffer = this.buffers.get(id)!;

        const node = this.categoryGains[category];
        if (node) source.connect(node);

        source.start(when ?? 0);
        source.onended = () => this.activeSources.delete(source);
        this.activeSources.add(source);

        return true;
    }

    public cancelAll() {
        this.activeSources.forEach(source => {
            try {
                source.stop();
            } catch (e) {
                // Ignore errors (e.g. already stopped)
            }
        });
        this.activeSources.clear();

        this.timeouts.forEach(id => clearTimeout(id));
        this.timeouts = [];
    }

    // ─── SYNTHESIZED FALLBACKS ───────────────────────────

    private playTone(freq: number, type: OscillatorType, duration: number, category: AudioCategory = 'fx', vol = 0.5) {
        if (!this.ctx) return;

        const t = this.ctx.currentTime;
        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();

        osc.type = type;
        osc.frequency.setValueAtTime(freq, t);

        gain.gain.setValueAtTime(0, t);
        gain.gain.linearRampToValueAtTime(vol, t + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, t + duration);

        osc.connect(gain);
        const node = this.categoryGains[category];
        if (node) gain.connect(node);

        osc.start(t);
        osc.stop(t + duration);
        osc.onended = () => this.activeSources.delete(osc);
        this.activeSources.add(osc);
    }

    private playInterval(base: number, harmonic: number) {
        this.playTone(base, 'triangle', 0.6, 'fx', 0.3);
        setTimeout(() => this.playTone(harmonic, 'sine', 0.6, 'fx', 0.3), 50);
    }

    /** Synth whistle fallback (bandpass filtered noise) */
    private synthWhistle() {
        if (!this.ctx) return;
        const t = this.ctx.currentTime;

        const bufLen = this.ctx.sampleRate * 2;
        const noiseBuf = this.ctx.createBuffer(1, bufLen, this.ctx.sampleRate);
        const data = noiseBuf.getChannelData(0);
        for (let i = 0; i < bufLen; i++) data[i] = Math.random() * 2 - 1;

        const src = this.ctx.createBufferSource();
        src.buffer = noiseBuf;
        src.loop = true;

        const filter = this.ctx.createBiquadFilter();
        filter.type = 'bandpass';
        filter.Q.value = 15;
        filter.frequency.setValueAtTime(2000, t);
        filter.frequency.linearRampToValueAtTime(1500, t + 0.1);
        filter.frequency.linearRampToValueAtTime(1800, t + 0.2);

        const gain = this.ctx.createGain();
        gain.gain.setValueAtTime(0, t);
        gain.gain.linearRampToValueAtTime(0.8, t + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.01, t + 0.3);

        src.connect(filter);
        filter.connect(gain);
        const node = this.categoryGains.fx;
        if (node) gain.connect(node);

        src.start(t);
        src.stop(t + 0.35);
        src.onended = () => this.activeSources.delete(src);
        this.activeSources.add(src);
    }

    /** Synth buzzer fallback (detuned sawtooths) */
    private synthBuzzer() {
        if (!this.ctx) return;
        const t = this.ctx.currentTime;
        const gain = this.ctx.createGain();
        gain.gain.setValueAtTime(0.5, t);
        gain.gain.linearRampToValueAtTime(0, t + 0.5);

        const node = this.categoryGains.fx;
        if (node) gain.connect(node);

        [150, 155].forEach(freq => {
            const osc = this.ctx!.createOscillator();
            osc.type = 'sawtooth';
            osc.frequency.value = freq;
            osc.connect(gain);
            osc.start(t);
            osc.stop(t + 0.5);
        });
    }

    /**
     * Transition: Subtle 2-tone upward shift.
     * 650Hz → 820Hz, 80ms each. Clean. Minimal. "Gear change."
     */
    private synthTransition() {
        if (!this.ctx) return;
        const t = this.ctx.currentTime;

        // First tone: 650Hz
        const osc1 = this.ctx.createOscillator();
        const g1 = this.ctx.createGain();
        osc1.type = 'sine';
        osc1.frequency.value = 650;
        g1.gain.setValueAtTime(0, t);
        g1.gain.linearRampToValueAtTime(0.25, t + 0.01);
        g1.gain.exponentialRampToValueAtTime(0.001, t + 0.1);
        osc1.connect(g1);
        const fxNode = this.categoryGains.fx;
        if (fxNode) g1.connect(fxNode);
        osc1.start(t);
        osc1.stop(t + 0.12);
        osc1.onended = () => this.activeSources.delete(osc1);
        this.activeSources.add(osc1);

        // Second tone: 820Hz (80ms later)
        const osc2 = this.ctx.createOscillator();
        const g2 = this.ctx.createGain();
        osc2.type = 'sine';
        osc2.frequency.value = 820;
        g2.gain.setValueAtTime(0, t + 0.08);
        g2.gain.linearRampToValueAtTime(0.3, t + 0.09);
        g2.gain.exponentialRampToValueAtTime(0.001, t + 0.22);
        osc2.connect(g2);
        if (fxNode) g2.connect(fxNode);
        osc2.start(t + 0.08);
        osc2.stop(t + 0.25);
        osc2.onended = () => this.activeSources.delete(osc2);
        this.activeSources.add(osc2);
    }

    /**
     * Rest Start: Calm descending tone. "Gear down. Recover."
     * 550Hz → 400Hz sweep over 300ms. Smooth, not dramatic.
     */
    private synthRestStart() {
        if (!this.ctx) return;
        const t = this.ctx.currentTime;

        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(550, t);
        osc.frequency.exponentialRampToValueAtTime(400, t + 0.25);

        gain.gain.setValueAtTime(0, t);
        gain.gain.linearRampToValueAtTime(0.3, t + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.35);

        osc.connect(gain);
        const node = this.categoryGains.fx;
        if (node) gain.connect(node);

        osc.start(t);
        osc.stop(t + 0.4);
        osc.onended = () => this.activeSources.delete(osc);
        this.activeSources.add(osc);
    }

    /**
     * Session Complete: Warm harmonic bell.
     * A4 (440) + E5 (660) + A5 (880) layered with soft attack and decay tail.
     * Classical. Ancient. Palaestra.
     */
    private synthComplete() {
        if (!this.ctx) return;
        const t = this.ctx.currentTime;
        const node = this.categoryGains.fx;

        [440, 660, 880].forEach((freq, i) => {
            const osc = this.ctx!.createOscillator();
            const gain = this.ctx!.createGain();
            osc.type = 'sine';
            osc.frequency.value = freq;

            // Soft attack, gentle decay — volume decreases for higher harmonics
            const vol = 0.25 - i * 0.05;
            gain.gain.setValueAtTime(0, t);
            gain.gain.linearRampToValueAtTime(vol, t + 0.03); // Quick attack
            gain.gain.setTargetAtTime(vol * 0.5, t + 0.10, 0.08); // Short sustain
            gain.gain.exponentialRampToValueAtTime(0.001, t + 0.40); // Tight tail

            osc.connect(gain);
            if (node) gain.connect(node);

            osc.start(t);
            osc.stop(t + 0.45);
            osc.onended = () => this.activeSources.delete(osc);
            this.activeSources.add(osc);
        });
    }

    // ─── PUBLIC API ──────────────────────────────────────

    /**
     * Play any cue. Tries sample first, falls back to synth.
     */
    public playCue(cue: CueId, when?: number) {
        this.resume();
        const category = CUE_CATEGORY[cue];

        // Try sample first
        if (this.playBuffer(cue, category, when)) return;

        // Synth fallbacks
        switch (cue) {
            // UI
            case 'ui_hover':
                this.playTone(800, 'sine', 0.05, 'ui', 0.05);
                break;
            case 'ui_press':
            case 'ui_tap':
                this.playTone(150, 'triangle', 0.1, 'ui', 0.3);
                break;
            case 'ui_success':
                this.playTone(440, 'sine', 0.3, 'ui', 0.2);
                setTimeout(() => this.playTone(554, 'sine', 0.3, 'ui', 0.2), 50);
                break;
            case 'ui_error':
                this.playTone(150, 'sawtooth', 0.3, 'ui', 0.3);
                this.playTone(140, 'sawtooth', 0.3, 'ui', 0.3);
                break;

            // Countdown (synth tick fallback)
            case 'count_5':
            case 'count_4':
            case 'count_3':
            case 'count_2':
            case 'count_1':
                this.playTone(1200, 'sine', 0.08, 'voice', 0.4);
                break;

            // Training FX
            case 'whistle':
                this.synthWhistle();
                break;
            case 'buzzer':
                this.synthBuzzer();
                break;
            case 'rest_start':
                this.synthRestStart();
                break;
            case 'transition':
                this.synthTransition();
                break;
            case 'complete':
                this.synthComplete();
                break;

            // Pillar Identity
            case 'pillar_neutral':
                this.playInterval(440, 660);
                break;
            case 'pillar_top':
                this.playInterval(440, 523.25);
                break;
            case 'pillar_bottom':
                this.playInterval(440, 587.33);
                break;
            case 'pillar_defense':
                this.playInterval(440, 622.25);
                break;
            case 'pillar_conditioning':
                this.playInterval(220, 440);
                break;
        }
    }

    // ─── SCHEDULED SEQUENCES ─────────────────────────────

    /**
     * Schedule a precise 5-4-3-2-1-GO countdown using AudioContext timing.
     * Counts play through 'voice', whistle plays through 'fx'.
     * Zero drift — no setTimeout.
     */
    /**
     * Schedule a precise 5-4-3-2-1-GO countdown using AudioContext timing.
     * Aligns exactly to the remaining time.
     * @param remainingMs - Current remaining time in milliseconds
     */
    public scheduleCountdown(remainingMs: number) {
        this.resume();
        if (!this.ctx) return;

        const t = this.ctx.currentTime;
        // Calculate when 'count_5' should start (at exactly 5000ms remaining)
        // e.g. if remaining is 5500, start in 0.5s
        // e.g. if remaining is 4900, start in -0.1s (past)
        const offsetTo5s = (remainingMs - 5000) / 1000;
        const startTime = t + offsetTo5s;

        const counts: CueId[] = ['count_5', 'count_4', 'count_3', 'count_2', 'count_1'];

        counts.forEach((id, i) => {
            const when = startTime + i; // 5s, 4s, 3s, 2s, 1s marks

            // Only schedule if it's not already passed (allow small 100ms jitter)
            if (when < t - 0.1) return;

            if (!this.playBuffer(id, 'voice', when)) {
                // Synth fallback
                if (!this.ctx) return;
                const osc = this.ctx.createOscillator();
                const gain = this.ctx.createGain();
                osc.type = 'sine';
                osc.frequency.value = 1200;
                gain.gain.setValueAtTime(0.4, when);
                gain.gain.exponentialRampToValueAtTime(0.001, when + 0.08);
                osc.connect(gain);
                const node = this.categoryGains.voice;
                if (node) gain.connect(node);
                osc.start(when);
                osc.stop(when + 0.1);
                osc.onended = () => this.activeSources.delete(osc);
                this.activeSources.add(osc);
            }
        });

    }

    /**
     * Play the end-of-round buzzer.
     */
    public playRoundEnd() {
        this.playCue('buzzer');
        // Play "Rest" slightly after buzzer for clarity
        setTimeout(() => this.playCue('rest'), 500);
    }

    /**
     * Play the rest start cue. Calm descending tone.
     */
    public playRestStart() {
        this.playCue('rest_start');
        this.playCue('rest');
    }

    /**
     * Play the work↔rest transition cue.
     */
    public playTransition() {
        this.playCue('transition');
    }

    /**
     * Play the session complete cue.
     */
    public playSessionComplete() {
        this.playCue('complete');
        this.playCue('break');
    }

    // ─── STATE ───────────────────────────────────────────

    public get state() {
        return this.ctx?.state ?? 'uninitialized';
    }

    public get isPreloaded() {
        return this.preloaded;
    }

    // ─── SPEECH SYNTHESIS ────────────────────────────────

    public speak(text: string, rate = 1.2, pitch = 1.0) {
        if (!('speechSynthesis' in window)) return;

        // Cancel any pending speech
        window.speechSynthesis.cancel();

        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = rate;   // Faster for coaching cues
        utterance.pitch = pitch; // Slightly authoritative
        utterance.volume = 1.0;

        // Try to find a good voice
        const voices = window.speechSynthesis.getVoices();
        // Prefer a deep male voice or "Google US English"
        const preferred = voices.find(v =>
            v.name.includes('Google US English') ||
            v.name.includes('David') ||
            v.name.includes('Male')
        );
        if (preferred) utterance.voice = preferred;

        window.speechSynthesis.speak(utterance);
    }
}

export const audioEngine = new AudioEngine();
